import { React, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { TextField, Button, Box, Typography, FormControlLabel, Switch } from "@mui/material";
import ModalPopup from "../../components/ModalPopup";
import CustomerForm from "./Forms/CustomerForm";
import { api } from "../../utils/api";
import CustomerList from "./CustomerList";
import usePriceChanges from "../../hooks/usePriceChanges";
import EmptyState from "../EmptyStates/EmptyState";

const Customer = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const limit = 100;
  const priceChanges = usePriceChanges(
    "customer",
    customers.map((customer) => customer._id),
    sessionStorage.getItem("selectedPortal") === "Administrator"
  );

  useEffect(() => {
    fetchCustomers(1);
  }, []);

  useEffect(() => {
    // Debounce search - wait 500ms after user stops typing
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      fetchCustomers(1, search);
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [search]);

  useEffect(() => {
    // Refetch when showDeleted toggle changes (reset to page 1)
    if (sessionStorage.getItem("selectedPortal") === "Administrator") {
      setCurrentPage(1);
      fetchCustomers(1, search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  const fetchCustomers = async (page = 1, searchTerm = "") => {
    try {
      setLoading(true);
      const currentPortal = sessionStorage.getItem("selectedPortal");
      
      let response;
      
      // Use search endpoint for Administrator portal
      if (currentPortal === "Administrator") {
        const query = {};
        if (searchTerm) {
          query.searchTerm = searchTerm;
        }
        // Include deleted customers if toggle is on
        if (showDeleted) {
          query.includeDeleted = true;
        }
        response = await api("v1/customer/search", "POST", {
          query,
          page,
          limit,
        });
      } else {
        // For Partner and Merchant, use existing endpoints (no search/pagination yet)
        switch (currentPortal) {
          case "Partner":
            let idPartnerCompany = sessionStorage.getItem("selectedCompany");
            response = await api(`v1/partner/company/${idPartnerCompany}/customers`, "GET");
            break;
          case "Merchant":
            const selectedMerchantStore = sessionStorage.getItem("selectedMerchantStore");
            if (selectedMerchantStore) {
              response = await api(`v1/merchantStore/${selectedMerchantStore}/customers`, "GET");
            }
            break;
          default:
            response = null;
            break;
        }
      }

      if (response?.data?.success) {
        if (currentPortal === "Administrator" && response.data.data.searchResult) {
          setCustomers(response.data.data.searchResult);
          setPagination(response.data.data.pagination);
          setCurrentPage(page);
        } else {
          setCustomers(response.data.data.allCustomers || []);
          setPagination(null);
        }
      }
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setLoading(false);
    }
  };

  const triggerCustomerListUpdate = (newCustomer) => {
    setShowCustomerForm(false);
    // Refresh the customer list after adding a new customer
    fetchCustomers(currentPage, search);
  };

  const handlePageChange = (newPage) => {
    fetchCustomers(newPage, search);
  };

  const AddNewCustomer = () => (
    <button
      className={`bg-darkblue text-white rounded-md py-2 px-4`}
      onClick={() => {
        setShowCustomerForm(true);
        setSearch("");
      }}
    >
      ADD NEW CUSTOMER
    </button>
  );

  return (
    <div className="page customer w-full flex flex-column items-center p-8">
      <div className="pageTitle flex flex-col w-full text-center lg:flex-row lg:text-left lg:justify-between mb-4">
        <h1>Customers</h1>
        <div>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ marginRight: "6px", width: "400px" }}
            variant="outlined"
            size="small"
            placeholder="Search for a customer"
          />
          {sessionStorage.getItem("selectedPortal") == "Administrator" && (
            <>
              <button
                className={`bg-white text-darkblue border border-darkblue rounded-md py-2 px-4 mr-2`}
                onClick={() => navigate("/customer/find-merge")}
              >
                FIND & MERGE
              </button>
              <AddNewCustomer />
            </>
          )}
        </div>
      </div>
      {loading ? (
        <GlobalLoadingSpinner />
      ) : (
        <div className="w-full flex flex-column items-center">
          {sessionStorage.getItem("selectedPortal") == "Administrator" && (
            <div className="pageActions">
              <ModalPopup
                showModal={showCustomerForm}
                handleShowModal={() => setShowCustomerForm(false)}
                popupTitle="Add New Customer"
                popupContent={<CustomerForm callback={triggerCustomerListUpdate} />}
              />
            </div>
          )}
          {customers.length === 0 ? (
            <EmptyState
              type="customers"
              text={search ? "No customers match the search" : "Adding customers is easy"}
              action={sessionStorage.getItem("selectedPortal") == "Administrator" ? <AddNewCustomer /> : null}
            />
          ) : (
            <>
              {sessionStorage.getItem("selectedPortal") == "Administrator" && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", mb: 2, maxWidth: "1200px" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showDeleted}
                        onChange={(e) => setShowDeleted(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Show Deleted Customers"
                  />
                </Box>
              )}
              <CustomerList customers={customers} priceChanges={priceChanges} />
              {pagination && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3, mb: 2 }}>
                  <Button
                    variant="outlined"
                    disabled={currentPage === 1 || loading}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2">
                    Page {currentPage} of {pagination.totalPages} ({pagination.totalRecords} total)
                  </Typography>
                  <Button
                    variant="outlined"
                    disabled={currentPage >= pagination.totalPages || loading}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Customer;
